<template>
  <div class="dropdown-button">
    <button
      id="user-dropdown-launcher"
      role="menuitem"
      aria-haspopup="true"
      aria-controls="app-bar-account-menu"
      :aria-expanded="xUserMenu"
      class="app-bar-button"
      :title="__('Account')"
      @click="showUserMenu"
    >
      <avatar-image :user="user" :width="20" :height="20" />
    </button>
    <div v-if="xUserMenu" class="dropdown-menu">
      <div class="dropdown dropdown-with-curtain user-dropdown" @click.self="cancel">
        <div id="app-bar-account-menu" role="menu" class="dropdown-content tv" :data-premium="isPremium">
          <div class="tv-section-divider app-bar-account-summary">
            <avatar-image :user="user" :width="40" :height="40" />
            <div class="strong-type">{{ displayName }}</div>
            <div class="small-type caption-type">{{ user.username }}</div>
            <template v-if="showQuota">
              <div class="quota-text small-type">{{ quotaUsageText }}</div>
              <div v-if="quotaUsed > 1.0" class="quota-text small-type">{{ quotaExcessUsageText }}</div>
              <progress-determinate class="quota-progress" :progress="quotaUsed" />
              <div class="quota-actions">
                <a
                  role="button"
                  class="buttony-caption-type"
                  :style="{ visibility: showReferrals ? 'visible' : 'hidden' }"
                  href="/dashboard/settings/referrals"
                  data-app-target="blank"
                >
                  {{ __('Earn more') }}
                </a>
                <a role="button" class="buttony-type" @click="upgradePlan">{{ __('Upgrade') }}</a>
              </div>
            </template>
          </div>
          <div class="tv tv-list">
            <a role="menuitem" class="tv-cell tv-cell-default tv-cell-actionable" href="/" data-app-target="self">
              <base-icon name="home" />
              <div>{{ __('Home') }}</div>
            </a>
            <a
              role="menuitem"
              class="tv-cell tv-cell-default tv-cell-actionable"
              :href="profileUrl"
              data-app-target="blank"
            >
              <base-icon name="profile" />
              <div>{{ __('Profile') }}</div>
            </a>
            <a
              role="menuitem"
              class="tv-cell tv-cell-default tv-cell-actionable"
              href="/dashboard/drive"
              data-app-target="blank"
            >
              <base-icon name="folder" />
              <div>{{ __('Drive') }}</div>
            </a>
            <a
              role="menuitem"
              class="tv-cell tv-cell-default tv-cell-actionable"
              href="/dashboard/stats"
              data-app-target="blank"
            >
              <base-icon name="stats" />
              <div>{{ __('Stats') }}</div>
            </a>
            <a
              role="menuitem"
              class="tv-cell tv-cell-default tv-cell-actionable"
              href="/dashboard/settings"
              data-app-target="blank"
            >
              <base-icon name="settings" />
              <div>{{ __('Settings') }}</div>
            </a>
            <a
              v-if="isNotNativeTenant"
              role="menuitem"
              class="tv-cell tv-cell-default tv-cell-actionable"
              href="/org/import"
              data-app-target="blank"
            >
              <base-icon name="download" />
              <div>{{ __('Import') }}</div>
            </a>
          </div>
          <div class="tv-section-divider" />
          <div class="tv">
            <form role="menuitem" method="post" action="/auth/logout">
              <csrf-input />
              <button class="raised-button block-button" type="submit" @click="logOut">{{ __('Log out') }}</button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { mapActions, mapState } from 'vuex'
import AvatarImage from 'vuecomponents/avatar_image'
import BaseIcon from 'vuecomponents/base_icon'
import ProgressDeterminate from 'vuecomponents/progress_determinate'
import CsrfInput from 'vuecomponents/csrf_input'
import EscapeToCancelMixin from 'vuemixins/escape_to_cancel_mixin'
import OzIcon from 'library/v4/components/OzIcon.vue'

const A_LOT_OF_WALLS = 10000
export default {
  name: 'AppBarAccount',
  components: {
    AvatarImage,
    OzIcon,
    BaseIcon,
    CsrfInput,
    ProgressDeterminate,
  },
  mixins: [EscapeToCancelMixin],
  props: ['user'],
  computed: {
    ...mapState(['xUserMenu']),
    isUserRegistered: function () {
      return !!this.user.registered_at
    },
    showReferrals: function () {
      return this.isUserRegistered
    },
    displayName: function () {
      return this.user.name || this.user.username
    },
    profileUrl: function () {
      return `/${this.user.username}`
    },
    isPremium() {
      return this.user.paying
    },
    showQuota: function () {
      return this.user.quota.walls_limit < A_LOT_OF_WALLS
    },
    quotaUsed: function () {
      return this.user.quota.walls_used / this.user.quota.walls_limit
    },
    quotaUsageText: function () {
      return this.__('%{num} of %{limit} padlets used', {
        num: this.user.quota.walls_used,
        limit: this.user.quota.walls_limit,
      })
    },
    quotaExcessUsageText: function () {
      return this.__('You have exceeded your padlet limit by %{excess}. Consider upgrading.', {
        excess: this.user.quota.walls_used - this.user.quota.walls_limit,
      })
    },
    isNotNativeTenant: function () {
      return this.user.tenant_id !== 1
    },
  },
  methods: {
    ...mapActions(['showUserMenu', 'hideUserMenu']),
    ...mapActions('electronApp', ['logOut']),
    ...mapActions('plans', ['userTriggeredUpgrade']),
    cancel() {
      this.hideUserMenu()
    },
    upgradePlan() {
      this.hideUserMenu()
      this.userTriggeredUpgrade({ user: this.user })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './../../assets/stylesheets/3/modules/all';
.app-bar-account-summary {
  padding: 24px 16px 16px;
  background-color: transparent;
}
.dropdown-content {
  font-size: 16px;
  background-color: $default-background-color;
  @include standard-scheme;
}
.tv-list {
  background: white;
}
form {
  padding: 8px;
}
.quota-progress {
  background-color: $grey-4;
}
.quota-text {
  margin-top: 16px;
  margin-bottom: 8px;
}
.quota-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0 0;
}
[data-premium] .app-bar-account-summary {
  background-position: bottom -12px center;
  background-repeat: no-repeat;
  background-size: 100% auto;
  background-image: url('../images/landing_rocket.svg');
}
</style>
